import { useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import { useSetTemplateCategories, useTemplateCategories } from './queries';

const sameCategoryIds = (left: string[], right: string[]) =>
  left.length === right.length && left.every(categoryId => right.includes(categoryId));

/** Keeps the settings dialog's category draft until it is saved or discarded. */
export function useTemplateCategoriesDraft(templateId: string) {
  const { addToast } = useToast();
  const { data: savedCategories = [] } = useTemplateCategories(templateId);
  const setTemplateCategories = useSetTemplateCategories(templateId);
  const [draftCategoryIds, setDraftCategoryIds] = useState<string[] | null>(null);

  const savedCategoryIds = savedCategories.map(category => category.id);
  const categoryIds = draftCategoryIds ?? savedCategoryIds;

  const save = async (): Promise<boolean> => {
    const categoriesChanged =
      draftCategoryIds !== null && !sameCategoryIds(draftCategoryIds, savedCategoryIds);
    if (!categoriesChanged) {
      setDraftCategoryIds(null);
      return true;
    }
    return setTemplateCategories.mutateAsync({ templateId, categoryIds: draftCategoryIds }).then(
      () => {
        setDraftCategoryIds(null);
        return true;
      },
      (error: Error) => {
        addToast({
          title: 'Could not save categories',
          description: error.message,
          variant: 'destructive',
        });
        return false;
      },
    );
  };

  const discard = () => setDraftCategoryIds(null);

  return {
    categoryIds,
    setCategoryIds: setDraftCategoryIds,
    save,
    discard,
    isSaving: setTemplateCategories.isPending,
  };
}
